import classNames from 'classnames';
import React, { useCallback, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Initial focus prefers a real form field. FOCUSABLE's first match is the header's close
// button, so opening "Add playlist" parked the caret on X instead of the name input.
const FIELD = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled])';
const openPanels = [];
const originalInert = new Map();
let originalOverflow;

function syncModalBackground() {
  const top = openPanels[openPanels.length - 1];
  if (!top) {
    originalInert.forEach((value, node) => { node.inert = value; });
    originalInert.clear();
    document.body.style.overflow = originalOverflow;
    return;
  }
  [...document.body.children].forEach((node) => {
    if (!originalInert.has(node)) originalInert.set(node, node.inert);
    node.inert = !node.contains(top);
  });
}

// `center` used to be accepted and silently ignored — the panel is centred unconditionally
// now (flex on the backdrop), so callers no longer pass it.
export const Modal = ({
  title,
  ariaLabel,
  handleCloseModal,
  children,
  footerButtons,
  notice,
  className,
  maxWidth = 'md:w-[472px]',
  bodyClassName = 'flex min-h-0 flex-col overflow-y-auto px-6 pb-2',
  showCloseButton = true,
}) => {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const titleId = useId();

  const handleKeyDown = useCallback((e) => {
    if (openPanels[openPanels.length - 1] !== panelRef.current || e.defaultPrevented) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseModal?.();
      return;
    }
    // Focus trap — Tab used to escape to the page behind the dialog.
    if (e.key !== 'Tab' || !panelRef.current) return;
    const nodes = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
    if (!nodes.length) {
      e.preventDefault();
      panelRef.current.focus();
      return;
    }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && (document.activeElement === first || !nodes.includes(document.activeElement))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !nodes.includes(document.activeElement))) {
      e.preventDefault();
      first.focus();
    }
  }, [handleCloseModal]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Scroll lock, initial focus, and focus restore to whatever opened the modal.
  useEffect(() => {
    const opener = document.activeElement;
    if (!openPanels.length) originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Visible-only, matching the focus trap: the file dropzone renders a hidden <input
    // type="file"> that must never take focus. Falls back to any focusable for dialogs that
    // have no fields at all (confirmations).
    const panel = panelRef.current;
    openPanels.push(panel);
    syncModalBackground();
    const field = [...(panel?.querySelectorAll(FIELD) || [])].find((n) => n.offsetParent !== null);
    (field || [...panel.querySelectorAll(FOCUSABLE)].find((n) => n.offsetParent !== null) || panel).focus();
    return () => {
      openPanels.splice(openPanels.indexOf(panel), 1);
      syncModalBackground();
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-ui-scrim backdrop-blur-[5px]"
        onClick={handleCloseModal}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={!title ? ariaLabel : undefined}
        aria-labelledby={title ? titleId : undefined}
        className={classNames(
          // dvh: 90vh on iOS Safari is measured with the toolbar hidden, so the footer
          // buttons could sit behind it.
          'relative flex max-h-[90dvh] w-full flex-col overflow-hidden',
          'rounded-modal border border-ui-hair-2 bg-ui-elevated shadow-modal',
          maxWidth,
          className,
        )}
      >
        {(title || showCloseButton) && (
          <header className="flex flex-none items-center px-6 pt-6 pb-5">
            {title ? (
              <h2 id={titleId} className="flex-1 text-[18px] font-bold text-ui">{title}</h2>
            ) : (
              <div className="flex-1" />
            )}
            {handleCloseModal && showCloseButton && (
              <button
                type="button"
                aria-label={t('buttons.close')}
                onClick={handleCloseModal}
                className="flex h-11 w-11 flex-none items-center justify-center rounded text-ui-3 transition-colors duration-150 hover:text-ui focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring"
              >
                <X size={18} />
              </button>
            )}
          </header>
        )}
        {children && <div className={bodyClassName}>{children}</div>}
        {notice && <div className="flex-none px-6 pt-2">{notice}</div>}
        {footerButtons && (
          <footer className="flex flex-none px-6 pb-6 pt-4">
            <div className="ml-auto flex flex-wrap justify-end items-center gap-3">{footerButtons}</div>
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
};
